/**
 * GZOO AI Marketing - Auth Hook
 * Handles authentication state and operations
 */

'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { apiClient } from '@/lib/api/client';
import { clearAuthStorage } from '@/lib/api/authSession';

interface User {
  id: string;
  email: string;
  name?: string;
  full_name?: string;
  org_id: string;
  role: string;
  email_verified?: boolean;
  created_at?: string;
  current_organization_id?: string;
  has_launched_campaign?: boolean;
  onboarding_completed?: boolean;
  // Platform owner (super-admin). When true, the Platform / Tenant Management surface is shown.
  is_operator?: boolean;
  // `/auth/me` returns per-organization memberships (each with a role) rather
  // than a single top-level role; useIsAdmin derives admin status from these.
  organizations?: { id: string; name: string; role: string }[];
  // Resolved white-label branding for the current org (GZOO defaults + any override).
  active_branding?: Branding;
}

export interface Branding {
  app_name: string;
  logo_url: string;
  logo_icon_url: string;
  favicon_url: string;
  primary_color: string;
  accent_color: string;
  background_color: string;
  is_custom?: boolean;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  email: string;
  password: string;
  full_name: string;
  organization_name?: string;
}

export function useAuth() {
  const router = useRouter();
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');

      if (!token) {
        setState({ user: null, isLoading: false, isAuthenticated: false });
        return;
      }

      try {
        // Verify token by fetching user profile
        const response = await apiClient.get('/auth/me');
        setState({
          user: response.data,
          isLoading: false,
          isAuthenticated: true,
        });
      } catch (error) {
        // Token invalid, clear it
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setState({ user: null, isLoading: false, isAuthenticated: false });
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await apiClient.post('/auth/login', credentials);
      const { access_token, refresh_token } = response.data;

      // Save tokens first
      localStorage.setItem('access_token', access_token);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }

      // Fetch user profile with explicit Authorization header
      const userResponse = await apiClient.get('/auth/me', {
        headers: {
          Authorization: `Bearer ${access_token}`
        }
      });
      const user = userResponse.data;

      setState({
        user,
        isLoading: false,
        isAuthenticated: true,
      });

      router.push('/dashboard');
      return { success: true };
    } catch (error: any) {
      setState(prev => ({ ...prev, isLoading: false }));
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  }, [router]);

  const register = useCallback(async (data: RegisterData) => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await apiClient.post('/auth/register', data);
      const { access_token, refresh_token, user } = response.data;

      localStorage.setItem('access_token', access_token);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }

      setState({
        user,
        isLoading: false,
        isAuthenticated: true,
      });

      router.push('/dashboard');
      return { success: true };
    } catch (error: any) {
      setState(prev => ({ ...prev, isLoading: false }));
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  }, [router]);

  const logout = useCallback(() => {
    clearAuthStorage(); // clears access/refresh + impersonation backups, so logout can't orphan op_backup_*
    setState({ user: null, isLoading: false, isAuthenticated: false });
    router.push('/login');
  }, [router]);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    try {
      const response = await apiClient.get('/auth/me');
      setState(prev => ({
        ...prev,
        user: response.data,
      }));
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  // Switch the active organization (tenant). The backend issues new tokens scoped to the
  // target org; we store them and do a FULL reload so every TanStack Query cache and page
  // re-fetches under the new org — guaranteeing no cross-tenant data bleed.
  const switchOrganization = useCallback(async (orgId: string) => {
    try {
      const response = await apiClient.post(`/auth/switch-organization/${orgId}`);
      const { access_token, refresh_token } = response.data;
      if (access_token) localStorage.setItem('access_token', access_token);
      if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
      window.location.assign('/dashboard');
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Could not switch organization',
      };
    }
  }, []);

  return {
    ...state,
    login,
    register,
    logout,
    refreshUser,
    switchOrganization,
  };
}
